import React from 'react'

import {
  Button, Divider, Edit, Icon, InfoOutlined, Refresh, TagComponent as Tag,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, oneOf, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import { formatLongDate } from 'Utils/FormatDate'
import { NOOP } from 'Utils/stableDefaults'

import BillingInfoItem from './BillingInfoItem'
import { PLAN_TYPE, SUBSCRIPTION_STATUS, resolveDisplayStatus } from '../../../constants'
import { RETRY_STATE } from '../../../hooks/useSubscription'

const PAYMENT_METHOD_PLACEHOLDER = '**** ****'

const STATUS_TAG_CONFIG = {
  [SUBSCRIPTION_STATUS.ACTIVE]: { color: 'primary', type: 'secondary' },
  [SUBSCRIPTION_STATUS.FULLY_CANCELED]: { color: 'grey', type: 'secondary' },
  [SUBSCRIPTION_STATUS.PENDING_CANCEL]: { color: 'warning', type: 'secondary' },
  [SUBSCRIPTION_STATUS.EXPIRED]: { color: 'grey', type: 'secondary' },
  [SUBSCRIPTION_STATUS.PAST_DUE]: { color: 'attention', type: 'secondary' },
  [SUBSCRIPTION_STATUS.REDEMPTION]: { color: 'attention', type: 'secondary' },
  [SUBSCRIPTION_STATUS.REJECTED]: { color: 'warning', type: 'secondary' },
  [SUBSCRIPTION_STATUS.SUSPENDED]: { color: 'warning', type: 'secondary' },
}

const DEFAULT_TAG = { color: 'grey', type: 'secondary' }

function PlanCard({
  statusLabel,
  status,
  canceledAt = null,
  canceledByAdmin = false,
  price,
  renewsOn = null,
  paymentMethod,
  subscriptionEndDate = null,
  isCancelLoading = false,
  onCancel: handleCancelSubscription = NOOP,
  onReactivate: handleReactivateSubscription = NOOP,
  onRenew: handleRenewSubscription = NOOP,
  isRestoreLoading = false,
  locale = 'en',
  updatePaymentUrl = '/profile/subscription/update-payment-method',
  onRetryPayment: handleRetryPayment = NOOP,
  retryState = RETRY_STATE.IDLE,
  planType = PLAN_TYPE.YEARLY,
  billingCycle = planType,
  onSwitchPlan: handleSwitchPlan = NOOP,
  scheduledPlanSwitchTo = null,
  scheduledPlanSwitchAt = null,
  onCancelPlanSwitch: handleCancelPlanSwitch = NOOP,
  isCancelPlanSwitchLoading = false,
}) {
  const displayStatus = resolveDisplayStatus(status, canceledAt)
  const isFullyCanceled = displayStatus === SUBSCRIPTION_STATUS.FULLY_CANCELED
  const isPendingCancel = displayStatus === SUBSCRIPTION_STATUS.PENDING_CANCEL
  const isSuspended = displayStatus === SUBSCRIPTION_STATUS.SUSPENDED
  const isInactive = isFullyCanceled || displayStatus === SUBSCRIPTION_STATUS.EXPIRED || isSuspended
  const canCancel = !isInactive && !isPendingCancel

  const tagConfig = STATUS_TAG_CONFIG[displayStatus] || DEFAULT_TAG
  const displayStatusLabel = (isFullyCanceled || isPendingCancel) ? 'Cancelled' : statusLabel
  const formattedEndDate = formatLongDate(subscriptionEndDate, locale)

  const translate = (key, options) => I18n.t(key, { ns: 'rails', lng: locale, ...options })
  const hasScheduledDowngrade = scheduledPlanSwitchTo === PLAN_TYPE.MONTHLY
  const hasAnyScheduledSwitch = Boolean(scheduledPlanSwitchTo)
  // When an upgrade to yearly is scheduled, show annual plan info immediately
  const displayPlanType = scheduledPlanSwitchTo === PLAN_TYPE.YEARLY ? PLAN_TYPE.YEARLY : planType
  const isMonthlyPlan = displayPlanType === PLAN_TYPE.MONTHLY
  const showRenewalInfo =
    !hasScheduledDowngrade &&
    displayStatus === SUBSCRIPTION_STATUS.ACTIVE &&
    (isMonthlyPlan || formattedEndDate)
  const priceForBanner = price.replace(' / month', '/mo')

  const hasActivePaymentMethod =
    Boolean(paymentMethod) && paymentMethod !== PAYMENT_METHOD_PLACEHOLDER
  const isRetryLoading = retryState === RETRY_STATE.LOADING
  const showRetryRow =
    displayStatus === SUBSCRIPTION_STATUS.REDEMPTION
    && hasActivePaymentMethod
    && retryState !== RETRY_STATE.SUBMITTED

  let planCardModifier = ''
  if (hasScheduledDowngrade) planCardModifier = ' plan-card--with-scheduled-switch'
  else if (showRenewalInfo) planCardModifier = ' plan-card--with-renewal'

  return (
    <div className={`plan-card${planCardModifier}`}>
      {hasScheduledDowngrade && (
        <div className="plan-card__scheduled-switch">
          <div className="plan-card__scheduled-switch-message">
            {/* SVG direct render to avoid Icon component color specificity conflict */}
            <InfoOutlined width="20" height="20" className="plan-card__scheduled-switch-icon" aria-hidden="true" />
            <p className="cgt-text-medium cgt-mb-0">
              <strong>{translate('subscription_management.scheduled_switch_banner.title')}</strong>
              {' - '}
              {translate('subscription_management.scheduled_switch_banner.description_prefix')}
              {' '}
              <strong>
                {translate(`subscription_management.switch_plan_modal.plan.${scheduledPlanSwitchTo}.name`)}
              </strong>
              {' '}
              {translate('subscription_management.scheduled_switch_banner.description_on')}
              {' '}
              <strong>{formatLongDate(scheduledPlanSwitchAt, locale)}</strong>
              {translate('subscription_management.scheduled_switch_banner.description_suffix', {
                price: priceForBanner,
              })}
            </p>
          </div>
          <Button
            type="secondary"
            size="small"
            customClass="plan-card__scheduled-switch-action plan-card__cancel-switch-button"
            onClick={handleCancelPlanSwitch}
            disabled={isCancelPlanSwitchLoading}
            loading={isCancelPlanSwitchLoading}
          >
            {translate('subscription_management.scheduled_switch_banner.cancel_button')}
          </Button>
        </div>
      )}
      {showRenewalInfo && (
        <div className="plan-card__renewal-info">
          <div className="plan-card__renewal-info__message">
            {/* SVG direct render to avoid Icon component color specificity conflict */}
            <InfoOutlined width="20" height="20" className="plan-card__renewal-icon" />
            <p className="cgt-text-medium cgt-mb-0">
              {isMonthlyPlan ? (
                <>
                  <strong>{translate('subscription_management.active.monthly_plan')}</strong>
                  {' - '}
                  {translate('subscription_management.active.monthly_renewal_message')}
                </>
              ) : (
                <>
                  <strong>{translate('subscription_management.active.annual_plan')}</strong>
                  {' - '}
                  {translate('subscription_management.active.renewal_message_prefix')}
                  <strong>{formattedEndDate}</strong>
                  {translate('subscription_management.active.renewal_message_suffix')}
                </>
              )}
            </p>
          </div>
          {!hasAnyScheduledSwitch && (
            <Button
              type="ghost-primary"
              size="small"
              customClass="plan-card__renewal-info__action"
              onClick={handleSwitchPlan}
              data-testid="plan-card-switch-plan-button"
            >
              {translate('subscription_management.active.switch_plan')}
            </Button>
          )}
        </div>
      )}

      <div className="plan-card__header">
        <div className="plan-card__title-group">
          <h3 className="cgt-legacy-text-default plan-card__title">
            {translate('subscription_management.active.plan_name')}
          </h3>
          <Tag
            color={tagConfig.color}
            type={tagConfig.type}
            small
          >
            {displayStatusLabel}
          </Tag>
        </div>
        {!canceledByAdmin && !isSuspended && (
          <>
            {isInactive && (
              <Button
                type="ghost-primary"
                size="small"
                onClick={handleReactivateSubscription}
                disabled={isRestoreLoading}
              >
                {translate('subscription_management.active.reactivate_button')}
              </Button>
            )}
            {isPendingCancel && (
              <Button
                type="ghost-primary"
                size="small"
                onClick={handleRenewSubscription}
                disabled={isRestoreLoading}
              >
                {translate('subscription_management.active.renew_button')}
              </Button>
            )}
            {canCancel && (
              <Button
                type="ghost"
                size="small"
                customClass="plan-card__cancel-button"
                onClick={handleCancelSubscription}
                disabled={isCancelLoading}
              >
                {translate('subscription_management.active.cancel_button')}
              </Button>
            )}
          </>
        )}
      </div>

      <Divider customClass="plan-card__divider" />

      <div className="plan-card__billing-grid">
        <BillingInfoItem
          iconType="billingCycle"
          label={translate('subscription_management.active.billing.billing_cycle')}
          value={translate(`subscription_management.billing_cycle.${billingCycle}`)}
        />
        <BillingInfoItem
          iconType="renewsOn"
          label={translate('subscription_management.active.billing.next_billing_date')}
          value={isSuspended
            ? translate('subscription_management.active.billing.billing_suspended')
            : (renewsOn || translate('subscription_management.active.billing.no_renewal'))}
        />
        <BillingInfoItem
          iconType="price"
          label={translate('subscription_management.active.billing.price')}
          value={price}
        />
        <BillingInfoItem
          iconType="payment"
          label={translate('subscription_management.active.billing.payment_method')}
          value={paymentMethod}
          action={canCancel && (
            <Button
              type="ghost"
              size="xsmall"
              icon={<Icon svgComponent={Edit} size="tiny" color="primary" />}
              customClass="billing-info-item__link"
              href={updatePaymentUrl}
            >
              {translate('subscription_management.active.billing.update')}
            </Button>
          )}
        />
      </div>

      {showRetryRow && (
        <>
          <Divider customClass="plan-card__divider" />
          <div className="plan-card__retry-row" aria-live="polite">
            <div className="plan-card__retry-row__action">
              <Button
                type="secondary-with-primary-border"
                size="small"
                customClass="plan-card__retry-row__button"
                icon={<Icon svgComponent={Refresh} size="tiny" color="primary" />}
                onClick={handleRetryPayment}
                disabled={isRetryLoading}
                aria-disabled={isRetryLoading}
                loading={isRetryLoading}
              >
                {isRetryLoading
                  ? translate('subscription_management.retry_payment.button_loading')
                  : translate('subscription_management.retry_payment.button')}
              </Button>
              <p className="plan-card__retry-row__helper cgt-legacy-text-default cgt-mb-0">
                {translate('subscription_management.retry_payment.helper_text')}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

PlanCard.propTypes = {
  statusLabel: string.isRequired,
  status: string.isRequired,
  canceledAt: string,
  canceledByAdmin: bool,
  price: string.isRequired,
  renewsOn: string,
  paymentMethod: string.isRequired,
  subscriptionEndDate: string,
  onCancel: func,
  isCancelLoading: bool,
  onReactivate: func,
  onRenew: func,
  isRestoreLoading: bool,
  locale: string,
  updatePaymentUrl: string,
  onRetryPayment: func,
  retryState: string,
  planType: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.YEARLY]),
  billingCycle: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.YEARLY]),
  onSwitchPlan: func,
  scheduledPlanSwitchTo: string,
  scheduledPlanSwitchAt: string,
  onCancelPlanSwitch: func,
  isCancelPlanSwitchLoading: bool,
}

export default PlanCard
